import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Sparkline from './Sparkline';
import { parseCurrencyValue } from '../utils/financialValueParse';

const ROLE_CLASS = {
    total: 'cfs-row--total',
    driver: 'cfs-row--driver',
    subtotal: 'cfs-row--subtotal',
    statementSubtotal: 'cfs-row--statement-subtotal',
    plain: 'cfs-row--plain',
    other: 'cfs-row--other',
    grandTotal: 'cfs-row--grand-total',
    supplementary: 'cfs-row--supplementary',
    groupLabel: 'cfs-row--group-label',
    divider: 'cfs-row--divider',
};

const NESTED_ROLES = new Set(['driver', 'subtotal', 'other', 'groupLabel']);

function parseSparklineNum(value) {
    const parsed = parseCurrencyValue(value);
    return parsed == null ? 0 : parsed;
}

function buildPseudoRatio(statementKey, label, yearlyValues, orderedKeys) {
    return {
        name: `financial.${statementKey}.${label}`,
        displayName: label,
        unit: '',
        description: `${label} from ${statementKey} statement`,
        formula: 'N/A',
        values: orderedKeys.map((year) => ({
            year,
            value: yearlyValues?.[year],
        })),
    };
}

function rowClassName(row) {
    const roleClass = ROLE_CLASS[row.role] || '';
    const sectionClass = row.sectionId ? `cfs-row--section-${row.sectionId}` : '';
    const depthClass = row.depth > 0 ? `cfs-row--depth-${row.depth}` : '';
    return ['cfs-row', roleClass, sectionClass, depthClass].filter(Boolean).join(' ');
}

function sectionsWithNestedRows(rows) {
    const ids = new Set();
    rows.forEach((row) => {
        if (row.sectionId && NESTED_ROLES.has(row.role)) {
            ids.add(row.sectionId);
        }
        if (row.parentSectionId && (NESTED_ROLES.has(row.role) || row.role === 'total')) {
            ids.add(row.parentSectionId);
        }
    });
    return ids;
}

/**
 * Shared hierarchical statement table used by Cash Flow, Profit & Loss, and Balance Sheet.
 */
function FinancialStatementTable({
    title,
    data,
    orderedKeys = [],
    headers = [],
    onMetricInfo,
    onOpenChart,
    buildRows,
    isSectionCollapsible,
    isRowVisible,
    defaultExpanded = {},
    statementKey,
    dividerNote,
    banner,
}) {
    const [expanded, setExpanded] = useState(defaultExpanded);

    const rows = useMemo(() => buildRows(data), [buildRows, data]);
    const nestedSectionIds = useMemo(() => sectionsWithNestedRows(rows), [rows]);

    const toggleSection = (sectionId) => {
        if (!isSectionCollapsible(sectionId)) return;
        setExpanded((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
    };

    if (!data || Object.keys(data).length === 0) {
        return <p>No data available.</p>;
    }

    const colCount = 2 + orderedKeys.length;

    return (
        <div className="ratios-section cfs-statement">
            <h3>{title}</h3>
            {banner}
            <div className="table-wrapper">
                <div className="table-scroll">
                    <table style={{ '--year-count': orderedKeys.length }}>
                        <thead>
                            <tr>
                                <th>Label</th>
                                <th>Trend</th>
                                {headers.map((header) => (
                                    <th key={header}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const hidden = !isRowVisible(row, expanded);

                                if (row.kind === 'divider') {
                                    return (
                                        <tr
                                            key={`cfs-divider-${row.label}`}
                                            className={rowClassName(row)}
                                            hidden={hidden}
                                        >
                                            <td colSpan={colCount}>
                                                <div className="cfs-derived-heading">{row.label}</div>
                                                {dividerNote ? (
                                                    <p className="cfs-derived-note">{dividerNote}</p>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                }

                                if (row.kind === 'groupLabel') {
                                    return (
                                        <tr
                                            key={`cfs-group-${row.sectionId || 'unclassified'}-${row.label}`}
                                            className={rowClassName(row)}
                                            hidden={hidden}
                                        >
                                            <td colSpan={colCount}>
                                                <span className="cfs-group-label">{row.label}</span>
                                            </td>
                                        </tr>
                                    );
                                }

                                const yearlyValues = data[row.label] || {};
                                const series = orderedKeys.map((year) => parseSparklineNum(yearlyValues[year]));
                                const pseudoRatio = buildPseudoRatio(
                                    statementKey,
                                    row.label,
                                    yearlyValues,
                                    orderedKeys,
                                );
                                const sectionOpen = row.sectionId ? expanded[row.sectionId] !== false : true;
                                const canToggle = row.role === 'total'
                                    && isSectionCollapsible(row.sectionId)
                                    && nestedSectionIds.has(row.sectionId);
                                const supplementaryTitle = row.role === 'supplementary' ? dividerNote : undefined;

                                return (
                                    <tr key={row.label} className={rowClassName(row)} hidden={hidden}>
                                        <td>
                                            <div className="ratio-name-cell">
                                                {canToggle ? (
                                                    <button
                                                        type="button"
                                                        className="cfs-toggle"
                                                        aria-expanded={sectionOpen}
                                                        aria-label={`${sectionOpen ? 'Collapse' : 'Expand'} ${row.label} details`}
                                                        onClick={() => toggleSection(row.sectionId)}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={sectionOpen ? 'chevron-down' : 'chevron-right'}
                                                            className="cfs-toggle-icon"
                                                            aria-hidden
                                                        />
                                                        <span>{row.label}</span>
                                                    </button>
                                                ) : (
                                                    <span title={supplementaryTitle}>{row.label}</span>
                                                )}
                                                <div className="ratio-actions" style={{ display: 'flex', gap: '6px' }}>
                                                    <FontAwesomeIcon
                                                        icon="circle-info"
                                                        title="View AI-powered definition"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onMetricInfo?.(row.label);
                                                        }}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon="chart-line"
                                                        title="Open chart"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onOpenChart?.(pseudoRatio);
                                                        }}
                                                    />
                                                    <FontAwesomeIcon
                                                        icon="arrow-trend-up"
                                                        title="Trend"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            onOpenChart?.(pseudoRatio);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td><Sparkline values={series} /></td>
                                        {orderedKeys.map((year) => (
                                            <td key={year}>{yearlyValues[year] ?? '-'}</td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default FinancialStatementTable;
